import {
  ButtonFacetedFilter,
  FacetedFilterOption,
} from "@/components/button-faceted-filter";
import DataTableAdminUsersHistory from "@/components/data-table-admin-users-history/data-table-admin-users-history";
import { DatePickerWithRange } from "@/components/date-range-picker";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Separator } from "@/components/ui/separator";
import { useToastError } from "@/components/ui/use-toast";
import { useApi } from "@/hooks/useApi";
import { useTranslation } from "@/hooks/useTranslation";
import { translateUserHistoryAction } from "@/translation/user-history.translation";
import { ReloadIcon } from "@radix-ui/react-icons";
import { AdminUserHistoryDTO } from "@shaarp/common";
import { useEffect, useState } from "react";
import { DateRange } from "react-day-picker";

export default function AdministrationUsersHistoryPage() {
  const api = useApi();
  const { t } = useTranslation();
  const toastError = useToastError();

  const [usersHistory, setUsersHistory] = useState<AdminUserHistoryDTO[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [dateRange, setDateRange] = useState<DateRange>({
    from: new Date(new Date().setHours(0, 0, 0, 0)), // today
    to: new Date(),
  });

  const [teamOptions, setTeamOptions] = useState<FacetedFilterOption[]>([]);
  const [teamsSelected, setTeamsSelected] = useState<string[]>([]);

  const [usersOptions, setUsersOptions] = useState<FacetedFilterOption[]>([]);
  const [usersSelected, setUsersSelected] = useState<string[]>([]);

  const [actionsOptions, setActionsOptions] = useState<FacetedFilterOption[]>(
    []
  );
  const [actionsSelected, setActionsSelected] = useState<string[]>([]);

  const [includeAdmins, setIncludeAdmins] = useState(false);

  const fetchData = async () => {
    if (!dateRange.from || !dateRange.to) {
      return;
    }

    setIsLoading(true);
    try {
      const { data } = await api.get<AdminUserHistoryDTO[]>(
        `/admin/users-history`,
        {
          params: {
            from: new Date(dateRange.from.setHours(0, 0, 0, 0)),
            to: new Date(dateRange.to.setHours(23, 59, 59, 999)),
            includeAdmins,
          },
        }
      );
      setUsersHistory(data);

      const teams = data.map((userHistory) => userHistory.team);
      const allUniquesTeamsId = teams
        .filter((team) => team !== null)
        .map((team) => team.id)
        .filter((value, index, self) => self.indexOf(value) === index);
      const tOptions: FacetedFilterOption[] = [
        {
          label: t("admin.usersHistory.unknown.team"),
          value: "unknown",
          total: teams.filter((t) => t === null).length,
        },
        ...allUniquesTeamsId.map((teamId) => {
          const team = teams.find((t) => t?.id === teamId);
          return {
            label: team?.name || t("admin.usersHistory.unknown.team"),
            value: team?.id || "unknown",
            total: teams.filter((t) => t?.id === teamId).length,
          };
        }),
      ].sort((a, b) => b.total - a.total);
      setTeamOptions(tOptions);

      const users = data.map((userHistory) => userHistory.user);
      const allUniquesUsersId = users
        .map((user) => user.id)
        .filter((value, index, self) => self.indexOf(value) === index);
      const uOptions: FacetedFilterOption[] = allUniquesUsersId
        .map((userId) => {
          const user = users.find((u) => u.id === userId);
          return {
            label: user?.fullName || t("admin.usersHistory.unknown.user"),
            value: user?.id || "unknown",
            total: users.filter((u) => u.id === userId).length,
          };
        })
        .sort((a, b) => b.total - a.total);
      setUsersOptions(uOptions);

      const actions = data.map((userHistory) => userHistory.action);
      const allUniquesActions = actions.filter(
        (value, index, self) => self.indexOf(value) === index
      );
      const aOptions: FacetedFilterOption[] = allUniquesActions
        .map((action) => {
          return {
            label: translateUserHistoryAction(action, t),
            value: action,
            total: actions.filter((a) => a === action).length,
          };
        })
        .sort((a, b) => b.total - a.total);
      setActionsOptions(aOptions);
    } catch (error) {
      toastError();
      setUsersHistory([]);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [dateRange, includeAdmins]);

  return (
    <div className="overflow-auto absolute top-0 left-0 flex-1 p-8 pt-6 compact:p-6 compact:pt-4 space-y-4 w-full h-full">
      <div className="flex-1 p-8 pt-0 space-y-5">
        <div className="flex justify-between">
          <div className="space-y-2">
            <h2 className="text-2xl font-bold tracking-tight">
              {t("admin.usersHistory.title")}
            </h2>
            <p className="text-muted-foreground">
              {t("admin.usersHistory.subtitle")}
            </p>
          </div>
          <div className="flex gap-3 justify-end">
            <DatePickerWithRange
              defaultDate={dateRange}
              onChange={(value) => setDateRange(value as DateRange)}
            />
            <Button variant="outline" onClick={fetchData} disabled={isLoading}>
              <ReloadIcon className="mr-2 w-4 h-4" />
              {t("admin.usersHistory.actions.refresh")}
            </Button>
          </div>
        </div>

        <Separator className="my-6" />

        <div className="flex gap-3">
          <ButtonFacetedFilter
            title={t("admin.usersHistory.filters.teams")}
            compacted={false}
            options={teamOptions}
            selected={teamsSelected}
            onChange={setTeamsSelected}
          />
          <ButtonFacetedFilter
            title={t("admin.usersHistory.filters.users")}
            compacted={false}
            options={usersOptions}
            selected={usersSelected}
            onChange={setUsersSelected}
          />
          <ButtonFacetedFilter
            title={t("admin.usersHistory.filters.actions")}
            compacted={false}
            options={actionsOptions}
            selected={actionsSelected}
            onChange={setActionsSelected}
          />
          <div className="flex items-center space-x-2">
            <Checkbox
              id="except-admins"
              checked={includeAdmins}
              onCheckedChange={(checked) => {
                setIncludeAdmins(checked === true);
              }}
            />
            <label
              htmlFor="except-admins"
              className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
            >
              {t("admin.usersHistory.filters.includeAdmins")}
            </label>
          </div>
        </div>

        <DataTableAdminUsersHistory
          isLoading={isLoading}
          usersHistory={usersHistory
            .filter((userHistory) => {
              if (teamsSelected.length > 0) {
                return teamsSelected.includes(
                  userHistory.team?.id || "unknown"
                );
              }
              return true;
            })
            .filter((userHistory) => {
              if (usersSelected.length > 0) {
                return usersSelected.includes(userHistory.user.id);
              }
              return true;
            })
            .filter((userHistory) => {
              if (actionsSelected.length > 0) {
                return actionsSelected.includes(userHistory.action);
              }
              return true;
            })}
        />
      </div>
    </div>
  );
}
